import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { LOGO } from "@/constants/images";

const LINKS = [
  { to: "/", label: "Home", testid: "nav-home-link" },
  { to: "/ceremonies", label: "Ceremonies", testid: "nav-ceremonies-link" },
  { to: "/pricing", label: "Pricing", testid: "nav-pricing-link" },
  { to: "/about", label: "About", testid: "nav-about-link" },
  { to: "/faq", label: "FAQ", testid: "nav-faq-link" },
  { to: "/contact", label: "Contact", testid: "nav-contact-link" },
];

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || open;

  return (
    <header
      data-testid="main-navigation"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow] duration-500 ${
        solid ? "bg-cream/95 shadow-[0_1px_0_rgba(201,168,76,0.35)] backdrop-blur-md" : "bg-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4 sm:px-10">
        <Link to="/" data-testid="nav-logo-link" aria-label="Scottish Voice and Verse, home" className="block shrink-0">
          <img
            src={LOGO}
            alt="Scottish Voice and Verse logo"
            data-testid="nav-logo-image"
            className={`w-auto mix-blend-multiply transition-[height] duration-500 ${solid ? "h-24 sm:h-24" : "h-28 sm:h-32"}`}
          />
        </Link>

        <nav className="hidden items-center gap-5 lg:flex lg:gap-7">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `group relative text-[12px] uppercase tracking-[0.2em] transition-colors duration-500 ${
                  solid ? "text-ink/70 hover:text-forest" : "text-cream/90 hover:text-cream"
                } ${isActive ? "font-medium" : ""}`
              }
            >
              {({ isActive }) => (
                <>
                  {l.label}
                  <span
                    className={`absolute -bottom-1.5 left-0 h-px bg-gold transition-[width] duration-500 ${
                      isActive ? "w-full" : "w-0 group-hover:w-full"
                    }`}
                  />
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <button
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
          onClick={() => setOpen(!open)}
          className={`lg:hidden ${solid ? "text-forest" : "text-cream"}`}
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <nav className="border-t border-gold/30 bg-cream px-6 pb-8 pt-4 lg:hidden" data-testid="mobile-menu">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`mobile-${l.testid}`}
              className={({ isActive }) =>
                `font-display block border-b border-forest/10 py-4 text-2xl ${isActive ? "text-mulberry" : "text-forest"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
};

export default Navbar;
